import { useTranslation } from 'react-i18next';

interface FooterProps {
  lang?: string;
  internalAccessibility?: boolean;  // Default: false (extern)
}

export default function REDFooter({
  lang,
  internalAccessibility = false
}: FooterProps) {
  const { t, i18n } = useTranslation('common');
  const currentLang = lang || i18n.language;

  /**
   * Helper: Gibt korrekte Sprache basierend auf Verfügbarkeit zurück
   * @param availableInAllLangs - true = alle 6 Sprachen, false = nur DE/EN
   */
  const getLangWithFallback = (availableInAllLangs: boolean): string => {
    if (availableInAllLangs) return currentLang;
    // Nur DE/EN verfügbar → Rest fällt auf EN
    return (currentLang === 'de' || currentLang === 'en') ? currentLang : 'en';
  };

  /**
   * Helper: Löst Link-Funktionen oder Maps auf
   */
  const resolveLink = (
    linkDef: string | ((lang: string) => string) | Record<string, string>,
    lang: string
  ): string => {
    if (typeof linkDef === 'string') return linkDef;
    if (typeof linkDef === 'function') return linkDef(lang);
    // Language Map
    return linkDef[lang] || linkDef['en'] || '';
  };

  // Barrierefreiheit-Link: intern ODER extern
  const accessibilityLink = internalAccessibility
    ? {
        // Intern: /{lang}/barrierefreiheit (alle Sprachen verfügbar)
        href: (lang: string) => `/${lang}/barrierefreiheit`,
        target: '_self' as const,
        availableInAllLangs: true
      }
    : {
        // Extern: DB-Website (komplexe URLs)
        href: {
          de: 'https://www.bahn.de/impressum/informationen-barrierefreiheit',
          en: 'https://int.bahn.de/en/site-notice/information-accessibility',
          fr: 'https://int.bahn.de/fr/mentions-legales/informations-laccessibilite',
          it: 'https://int.bahn.de/it/note-legali/informazioni-accessibilita',
          es: 'https://int.bahn.de/es/pie-de-imprenta/informacion-accesibilidad',
          pl: 'https://int.bahn.de/pl/impressum/informacje-dostepnosci'
        },
        target: '_blank' as const,
        availableInAllLangs: true
      };

  const footerLinks = [
    {
      label: t('footer.links.imprint'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/link_info_impressum_pk_de'
        : `https://www.bahn.de/link_info_impressum_pk_${lang}`,
      target: '_blank' as const,
      availableInAllLangs: true,
      isAnchor: true
    },
    {
      label: t('footer.links.accessibility'),
      href: accessibilityLink.href,
      target: accessibilityLink.target,
      availableInAllLangs: accessibilityLink.availableInAllLangs,
      isAnchor: true
    },
    {
      label: t('footer.links.termsOfCarriage'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/link_buchung_agb_pk_de'
        : `https://www.bahn.de/link_buchung_agb_pk_${lang}`,
      target: '_blank' as const,
      availableInAllLangs: true,
      isAnchor: true
    },
    {
      label: t('footer.links.privacy'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/link_info_datenschutz_pk_de'
        : `https://www.bahn.de/link_info_datenschutz_pk_${lang}`,
      target: '_blank' as const,
      availableInAllLangs: true,
      isAnchor: true
    },
    {
      label: t('footer.links.termsOfUse'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/link_navigator_nutzungsbedingungen_pk_de'
        : `https://int.bahn.de/${lang}/terms-of-use`,
      target: '_blank' as const,
      availableInAllLangs: false,  // NUR DE/EN
      isAnchor: true
    },
    {
      label: t('footer.links.cancelContract'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/kontakt/kuendigungsformular'
        : `https://int.bahn.de/${lang}/help/contact/cancellation-form`,
      target: '_blank' as const,
      availableInAllLangs: false,  // NUR DE/EN
      isAnchor: true
    },
    {
      label: t('footer.links.lksg'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.bahn.de/info/lieferkettensorgfaltspflichtengesetz'
        : `https://int.bahn.de/${lang}/supply-chain-act`,
      target: '_blank' as const,
      availableInAllLangs: false,  // NUR DE/EN
      isAnchor: true
    },
    {
      label: t('footer.links.dbGroup'),
      href: (lang: string) => lang === 'de'
        ? 'https://www.deutschebahn.com/de'
        : `https://www.deutschebahn.com/${lang}`,
      target: '_blank' as const,
      availableInAllLangs: true,
      isAnchor: true
    },
    {
      label: t('footer.links.copyright'),
      href: '',
      target: '_self' as const,
      availableInAllLangs: true,
      isAnchor: false
    }
  ];

  return (
    <footer className="w-full mx-auto db-bg-color-basic-level-3 py-10 px-5 text-center text-sm">
      <ul className="list-none max-w-[1200px] inline-flex justify-center space-x-6 mx-auto flex-wrap">
        {footerLinks.map((link, index) => {
          const actualLang = getLangWithFallback(link.availableInAllLangs);
          const href = resolveLink(link.href, actualLang);

          return (
            <li key={index} className="leading-normal min-h-6">
              {link.isAnchor ? (
                <a
                  href={href}
                  className="hover:underline"
                  target={link.target}
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <span>{link.label}</span>
              )}
            </li>
          );
        })}
      </ul>
    </footer>
  );
}
